import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { getPermissions } from '../services/Users/userService';
import { jwtDecode } from 'jwt-decode';
import { tokenStorage, userDataStorage, permissionsStorage } from '../utils/encryptedCookie';

const PermissionsContext = createContext();

export const usePermissionsContext = () => {
  const context = useContext(PermissionsContext);
  if (!context) {
    throw new Error('usePermissionsContext deve ser usado dentro de PermissionsProvider');
  }
  return context;
};

export const PermissionsProvider = ({ children }) => {
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(false);
  const currentUserIdRef = useRef(null);

  const getUserIdFromToken = useCallback(() => {
    try {
      const token = tokenStorage.get();
      if (!token) return null;
      const decoded = jwtDecode(token);
      
      // Prioridade: userData.id (cookie) > decoded.userId (JWT claim) > decoded.id > decoded.sub
      const userData = userDataStorage.get();
      if (userData && userData.id) {
        return userData.id;
      }
      
      // O JWT tem userId como claim (não sub)
      if (decoded.userId) {
        return decoded.userId;
      }
      
      // Fallback para outros formatos
      if (decoded.id) {
        return decoded.id;
      }
      
      // decoded.sub é o username, não o ID - não usar como fallback
      return null;
    } catch (error) {
      return null;
    }
  }, []);

  const loadPermissions = useCallback(async () => {
    const userId = getUserIdFromToken();
    if (!userId) {
      setPermissions({});
      permissionsStorage.remove();
      currentUserIdRef.current = null;
      return;
    }

    // Se o userId mudou, limpa permissões antigas
    if (currentUserIdRef.current !== userId) {
      setPermissions({});
      permissionsStorage.remove();
      currentUserIdRef.current = userId;
    }
    
    setLoading(true);
    try {
      const perms = await getPermissions(userId);
      setPermissions(perms || {});
      permissionsStorage.set(perms || {});
    } catch (error) {
      // Em caso de erro, não usa permissões antigas do cookie
      // para evitar usar permissões de outro usuário
      setPermissions({});
      permissionsStorage.remove();
    } finally {
      setLoading(false);
    }
  }, [getUserIdFromToken]);

  const hasPermission = useCallback((permissionKey) => {
    if (!permissionKey) return false;
    // Se não houver permissões atribuídas (objeto vazio), permite acesso total
    const hasAnyPermissions = Object.keys(permissions).length > 0;
    if (!hasAnyPermissions) return true;
    // Se houver permissões, verifica normalmente
    return permissions[permissionKey] === true;
  }, [permissions]);

  const clearPermissions = useCallback(() => {
    setPermissions({});
    permissionsStorage.remove();
    currentUserIdRef.current = null;
  }, []);

  // Verifica mudanças no token/userId quando o componente monta e quando há mudanças
  useEffect(() => {
    const checkAndLoad = () => {
      const token = tokenStorage.get();
      const userId = getUserIdFromToken();
      
      if (token && userId) {
        // Se o userId mudou, recarrega permissões
        if (currentUserIdRef.current !== userId) {
          loadPermissions();
        }
      } else if (!token) {
        clearPermissions();
      }
    };

    // Verifica imediatamente
    checkAndLoad();

    // Verifica periodicamente para detectar mudanças de usuário (a cada 500ms)
    const interval = setInterval(checkAndLoad, 500);

    return () => clearInterval(interval);
  }, [getUserIdFromToken, loadPermissions, clearPermissions]);

  return (
    <PermissionsContext.Provider
      value={{
        permissions,
        loading,
        hasPermission,
        loadPermissions,
        clearPermissions,
      }}
    >
      {children}
    </PermissionsContext.Provider>
  );
};

